"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const EducationClient = () => {
    const { t, translationsLoaded } = useComponentTranslations('Education');
    const et = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: et('educationExpertise1'), icon: 'carbon:analytics' },
        { title: et('educationExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: et('educationExpertise3'), icon: 'carbon:rule' },
        { title: et('educationExpertise4'), icon: 'carbon:security' },
        { title: et('educationExpertise5'), icon: 'carbon:cloud-app' },
        { title: et('educationExpertise6'), icon: 'carbon:blockchain' },
        { title: et('educationExpertise7'), icon: 'carbon:data-refinery' },
        { title: et('educationExpertise8'), icon: 'carbon:chart-line-data' },
        { title: et('educationExpertise9'), icon: 'carbon:user-profile' },
    ];

    const useCasesForAccordion = [
        { title: et('educationUseCase1'), description: et('educationUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: et('educationUseCase2'), description: et('educationUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: et('educationUseCase3'), description: et('educationUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: et('educationUseCase4'), description: et('educationUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    const servicesForAlternating = [
        { title: et('educationService1Title'), description: et('educationService1Desc'), image: '/images/finance.jpg' },
        { title: et('educationService2Title'), description: et('educationService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: et('educationService3Title'), description: et('educationService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: et('educationService4Title'), description: et('educationService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: et('educationService5Title'), description: et('educationService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: et('educationFaq1q'), answer: et('educationFaq1a') },
        { question: et('educationFaq2q'), answer: et('educationFaq2a') },
        { question: et('educationFaq3q'), answer: et('educationFaq3a') },
        { question: et('educationFaq4q'), answer: et('educationFaq4a') },
        { question: et('educationFaq5q'), answer: et('educationFaq5a') },
    ];

    return (
        <div className="relative bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {et('educationHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {et('educationHeroSubtitle')}
                </motion.p>
            </header>

            <section className="w-full py-24 bg-white/5 border-y border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{et('educationPainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {et('educationPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{et('educationExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 border border-white/10 rounded-lg bg-white/5 flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={et('educationServicesTitle')} useCases={servicesForAlternating} />
            </div>

            <ServicesAccordion title={et('educationUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={et('educationTechTitle')} t={t} />
                <IndustrialsForm label={et('educationCtaTitle')} />

                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{et('educationFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 border border-white/10 rounded-lg bg-white/5">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default EducationClient;
